/**
 * Matrice des scores inbasket d'une campagne : un candidat par ligne, une
 * compétence du barème par colonne.
 *
 * Même patron que `PamiCampagneTable` : `DataTable` en largeurs `auto`, colonne
 * Candidat figée, le tout défilant horizontalement. Les compétences ne sont pas
 * connues à l'avance — elles se découvrent dans les résultats — d'où des
 * colonnes construites à chaque changement de données plutôt qu'un module.
 */
import {
  Card,
  CardContent,
  DataTable,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  filtreRecherche,
  type ColonneDataTable,
} from "@repo/ui";
import { categorieColors } from "@repo/utils";
import { useMemo } from "react";

interface InbasketEvalRow {
  _id: string;
  parcoursId: string;
  nomCandidat?: string;
  prenomCandidat?: string;
  clientResultsJson?: string;
  clientResultsGradesJson?: string;
}

interface InbasketCampagneTableProps {
  evaluations: InbasketEvalRow[];
}

/** Une ligne de la matrice : l'évaluation, ses résultats déjà décodés. */
type InbasketLigne = InbasketEvalRow & {
  clientResults?: Record<string, number>;
  clientResultsGrades?: Record<string, string>;
};

const RECHERCHE = {
  colonne: "candidat",
  placeholder: "Nom, prénom…",
  libelle: "Rechercher un candidat",
} as const;

const BARRE_FILTRES = { recherche: RECHERCHE };

function nomCandidat(ligne: InbasketLigne): string {
  return `${ligne.prenomCandidat ?? ""} ${ligne.nomCandidat ?? ""}`.trim();
}

export function InbasketCampagneTable({
  evaluations,
}: InbasketCampagneTableProps) {
  const lignes = useMemo<InbasketLigne[]>(
    () =>
      evaluations.map((evaluation) => ({
        ...evaluation,
        clientResults: evaluation.clientResultsJson
          ? (JSON.parse(evaluation.clientResultsJson) as Record<string, number>)
          : undefined,
        clientResultsGrades: evaluation.clientResultsGradesJson
          ? (JSON.parse(evaluation.clientResultsGradesJson) as Record<
              string,
              string
            >)
          : undefined,
      })),
    [evaluations],
  );

  // Compétences uniques, tous candidats confondus, dans l'ordre de première
  // apparition : le barème est celui du template, pas de tri alphabétique qui
  // le désordonnerait.
  const competences = useMemo(() => {
    const vues = new Set<string>();
    const ordre: string[] = [];
    for (const ligne of lignes) {
      for (const cle of Object.keys(ligne.clientResults ?? {})) {
        if (!vues.has(cle)) {
          vues.add(cle);
          ordre.push(cle);
        }
      }
    }
    return ordre;
  }, [lignes]);

  const colonnes = useMemo<ColonneDataTable<InbasketLigne>[]>(
    () => [
      {
        id: "candidat",
        header: "Candidat",
        accessorFn: nomCandidat,
        cell: ({ row }) => {
          const nom = nomCandidat(row.original);
          return nom ? (
            <span className="font-medium whitespace-nowrap">{nom}</span>
          ) : (
            <span className="text-muted-foreground italic">Anonyme</span>
          );
        },
        enableHiding: false,
        filterFn: filtreRecherche<InbasketLigne>((l) => [
          l.prenomCandidat,
          l.nomCandidat,
        ]),
        meta: { libelle: "Candidat", largeur: "12rem", figee: true },
      },
      ...competences.map<ColonneDataTable<InbasketLigne>>((competence) => ({
        id: competence,
        // `asChild` sur un <span> : l'en-tête triable est déjà un <button>, et
        // un bouton dans un bouton est du HTML invalide.
        header: () => (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="cursor-default block max-w-24 truncate">
                {competence}
              </span>
            </TooltipTrigger>
            <TooltipContent>{competence}</TooltipContent>
          </Tooltip>
        ),
        // `null` plutôt qu'`undefined` pour un score absent : TanStack range
        // les valeurs nulles en fin de tri au lieu de les mêler aux zéros.
        accessorFn: (l) => l.clientResults?.[competence] ?? null,
        cell: ({ row }) => {
          const score = row.original.clientResults?.[competence];
          const grade = row.original.clientResultsGrades?.[competence];
          const couleur = grade ? (categorieColors[grade] ?? "#6b7280") : undefined;
          return score !== undefined && couleur ? (
            <span
              className="inline-block rounded-pastille px-2 py-0.5 text-xs font-semibold text-surface tabular-nums"
              style={{ backgroundColor: couleur }}
            >
              {score.toFixed(2)}%
            </span>
          ) : (
            <span className="text-muted-foreground">—</span>
          );
        },
        meta: { libelle: competence, largeur: "6rem" },
      })),
    ],
    [competences],
  );

  if (evaluations.length === 0) {
    return (
      <Card>
        <CardContent className="py-8 text-center">
          <p className="text-muted-foreground">
            Aucune évaluation inbasket dans cette campagne.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <DataTable
      colonnes={colonnes}
      donnees={lignes}
      cleColonnes="campagne-inbasket"
      barreFiltres={BARRE_FILTRES}
      // Une matrice de scores se lit d'un bloc : la pagination couperait la
      // comparaison entre candidats, qui est tout l'objet de cette table.
      taillePage="toutes"
      largeurColonnes="auto"
      densite="compacte"
      videFiltre={{ titre: "Aucun candidat pour cette recherche" }}
    />
  );
}
